import {
  Select,
  Table,
  TableBody,
  TableHeader,
  TableHeading,
  TableRow,
  useFirstRender,
} from '@insurancemenu/imenu-components-lib';
import clsx from 'clsx';
import DateRange from 'components/common/general/DateRange';
import { toJS } from 'mobx';
import { observer } from 'mobx-react-lite';
import React, { useEffect } from 'react';
import { useRootStore } from 'store/rootStore';
import { GenerationStep } from 'types/policyService';
import PolicyInfoModal from './PolicyInfoModal';
import PolicyTableRow from './PolicyTableRow';
import { createStyles } from './styles';
import UpdatePolicyStepModal from './UpdatePolicyStepModal';

const PolicyDashboard = observer((): JSX.Element => {
  const classes = createStyles();
  const firstRender = useFirstRender();
  const {
    filesTrackingController: { getSponsorOptionsWithOptionAll },
    policyDashboardStore: {
      step,
      sponsorId,
      page,
      totalElements,
      totalPages,
      date: { from: dateFrom, to: dateTo },
      selectedPolicy,
      loading,
      policies,
      modalType,
      isModalOpen,
      updateDate,
      setSponsorId,
      getStepOptions,
      setStep,
      getPolicies,
      setPage,
    },
  } = useRootStore();

  const convertedSelectedPolicy = toJS(selectedPolicy);
  const convertedPolicies = policies.map((policy) => toJS(policy)).slice();

  useEffect(() => {
    void getPolicies();
  }, []);

  useEffect(() => {
    void getPolicies();
  }, [page]);

  useEffect(() => {
    if (!firstRender) {
      setPage(1);
      void getPolicies();
    }
  }, [sponsorId, step, dateFrom, dateTo]);

  const policiesList = convertedPolicies.map((file) => <PolicyTableRow key={file.id} file={file} />);

  const PolicyModal = (): JSX.Element => {
    switch (modalType) {
      case 'UPDATE_STEP':
        return <UpdatePolicyStepModal isModalOpen={isModalOpen} policy={convertedSelectedPolicy} />;

      case 'SHOW_INFO':
        return <PolicyInfoModal isModalOpen={isModalOpen} policy={convertedSelectedPolicy} />;

      default:
        return <></>;
    }
  };

  return (
    <div className={classes.content}>
      <div className={classes.actionHeader}>
        <div className={classes.dateRange}>
          <DateRange updateTableByDateRange={updateDate} showAdminToggle={false} />
        </div>
        <div className={classes.filters}>
          <Select
            label="Sponsor Id"
            name="sponsorId"
            options={getSponsorOptionsWithOptionAll()}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>): void => {
              setSponsorId(e.target.value);
            }}
            value={sponsorId}
            width="23rem"
          />
          <Select
            label="Generation Step"
            name="step"
            options={getStepOptions(true)}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>): void => {
              setStep(e.target.value as GenerationStep);
            }}
            value={step}
            width="23rem"
          />
        </div>
      </div>
      <p className={classes.infoTitle}>
        Total Policies:<b className={classes.totalAmount}>{totalElements}</b>
      </p>
      <div className={classes.tableWrapper}>
        <Table
          className={classes.table}
          loading={loading}
          maxHeight="55rem"
          minHeight="55rem"
          totalElements={totalElements}
          pagination={{ onPageChange: setPage, maxPage: totalPages || 1, tablePage: page }}
        >
          <TableHeader className={classes.tableHeader}>
            <TableRow>
              <TableHeading className={classes.tableHeading}>Sponsor Id</TableHeading>
              <TableHeading className={classes.tableHeading}>Group Name</TableHeading>
              <TableHeading className={classes.tableHeading}>Agency Name</TableHeading>
              <TableHeading className={classes.tableHeading}>Effective Date</TableHeading>
              <TableHeading className={classes.tableHeading}>Creation Date</TableHeading>
              <TableHeading className={classes.tableHeading}>Generation Step</TableHeading>
              <TableHeading className={clsx(classes.tableHeading, classes.buttonCell)} />
              <TableHeading className={clsx(classes.tableHeading, classes.buttonCell)} />
            </TableRow>
          </TableHeader>
          <TableBody className={classes.tableBody}>{policiesList}</TableBody>
        </Table>
      </div>
      {PolicyModal()}
    </div>
  );
});

export default PolicyDashboard;
